import { useState } from "react";
import type React from "react";
import { theme, card, btnPrimary, btnSecondary, inputStyle, type as text } from "../theme";
import { fmtDate } from "../lib/format";
import { api, apiErrorMessage } from "../api/client";

// "Review effectiveness" for a verified corrective action (ISO 27001 10.2 d, ISO 9001 10.2.1 d).
// Verification says the fix went in; this says, some time later, whether it held. A result of not
// effective offers to raise a follow-up action linked back by sourceRef "ca-followup:<id>", so the
// register keeps the failed attempt and the next one side by side. Rules are server-side
// (lib/correctiveActions.ts effectivenessStanding / effectivenessReviewError).

export type EffectivenessStanding = "not_applicable" | "scheduled" | "due" | "overdue" | "reviewed";

export interface EffectivenessAction {
  id: string;
  displayId: number | null;
  title: string;
  status: string;
  effectivenessDueAt: string | null;
  effectivenessReviewedAt: string | null;
  effectivenessResult: string | null;
  effectivenessNote: string | null;
  effectiveness: EffectivenessStanding;
}

const day = fmtDate;

/** Whether an action has an effectiveness review to show or record at all. */
export const hasEffectivenessReview = (a: EffectivenessAction) => a.status === "verified" || !!a.effectivenessReviewedAt;
/** Whether the review can be recorded now: verified and not yet reviewed. */
export const canRecordEffectiveness = (a: EffectivenessAction) => a.status === "verified" && !a.effectivenessReviewedAt;

const tone = (a: EffectivenessAction) => (a.effectiveness === "overdue" ? theme.color.dangerText : a.effectiveness === "due" ? theme.color.warningText : theme.color.subtle);

/** The review as it reads in the action's view: the result once recorded, otherwise when it is due. */
export function EffectivenessView({ action }: { action: EffectivenessAction }) {
  if (!hasEffectivenessReview(action)) return null;
  if (action.effectivenessReviewedAt) {
    const ok = action.effectivenessResult === "effective";
    return (
      <div>
        <div style={{ ...text.body, color: ok ? theme.color.successText : theme.color.dangerText, fontWeight: 600 }}>
          {ok ? "Effective" : "Not effective"} · reviewed {day(action.effectivenessReviewedAt)}
        </div>
        {action.effectivenessNote && <div style={{ ...text.body, marginTop: 6, whiteSpace: "pre-wrap" }}>{action.effectivenessNote}</div>}
      </div>
    );
  }
  return (
    <div style={{ ...text.body, color: tone(action), fontWeight: action.effectiveness === "scheduled" ? 400 : 600 }}>
      {action.effectivenessDueAt
        ? `Not yet reviewed. ${action.effectiveness === "overdue" ? "Was due" : "Due"} ${day(action.effectivenessDueAt)}.`
        : "Not yet reviewed."}
    </div>
  );
}

/** Recording the review: opened from the action view's Actions, never shown inline while reading. */
export function ActionEffectiveness({ action, onDone, onCancel }: {
  action: EffectivenessAction;
  /** Called after a review is saved, with the follow-up action's id when one was raised. */
  onDone: (followUpId: string | null) => void | Promise<void>;
  onCancel?: () => void;
}) {
  const [result, setResult] = useState<"" | "effective" | "not_effective">("");
  const [note, setNote] = useState("");
  const [raiseFollowUp, setRaiseFollowUp] = useState(true);
  const [followUpTitle, setFollowUpTitle] = useState(`Follow-up: ${action.title}`.slice(0, 300));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!canRecordEffectiveness(action)) return null;

  const box: React.CSSProperties = { ...card, marginTop: 14, padding: 14 };
  const heading = <div style={{ ...text.sectionTitle, marginBottom: 6 }}>Record the effectiveness review</div>;

  async function save() {
    if (!result) { setError("Choose whether the fix worked."); return; }
    if (note.trim().length < 3) { setError("Say how you checked whether it worked."); return; }
    setBusy(true); setError("");
    try {
      const res = await api.put<{ followUp: { id: string } | null }>(`/improvement/actions/${action.id}/effectiveness`, {
        result, note: note.trim(),
        ...(result === "not_effective" ? { raiseFollowUp, ...(raiseFollowUp && followUpTitle.trim() ? { followUpTitle: followUpTitle.trim() } : {}) } : {}),
      });
      await onDone(res.followUp?.id ?? null);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not record the review."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={box}>
      {heading}
      <div style={{ ...text.hint, color: tone(action), marginBottom: 10, fontWeight: action.effectiveness === "scheduled" ? 400 : 600 }}>
        {action.effectivenessDueAt
          ? `${action.effectiveness === "overdue" ? "Was due" : "Due"} ${day(action.effectivenessDueAt)}. Check that the problem has not come back, then record what you found.`
          : "Check that the problem has not come back, then record what you found."}
      </div>
      <div role="radiogroup" aria-label="Did the fix work?" style={{ display: "flex", gap: 16, marginBottom: 10, flexWrap: "wrap" }}>
        {([["effective", "It worked"], ["not_effective", "It did not work"]] as const).map(([v, label]) => (
          <label key={v} style={{ ...text.hint, color: theme.color.text, display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
            <input type="radio" name={`eff-${action.id}`} checked={result === v} onChange={() => setResult(v)} />
            {label}
          </label>
        ))}
      </div>
      <textarea
        aria-label="How it was checked"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="How you checked: the records, samples or tests you looked at."
        style={{ ...inputStyle, resize: "vertical", minHeight: 60 }}
      />
      {result === "not_effective" && (
        <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
          <label style={{ ...text.hint, color: theme.color.text, display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={raiseFollowUp} onChange={(e) => setRaiseFollowUp(e.target.checked)} />
            Raise a follow-up action, linked to this one
          </label>
          {raiseFollowUp && (
            <input aria-label="Follow-up title" value={followUpTitle} onChange={(e) => setFollowUpTitle(e.target.value)} style={inputStyle} />
          )}
        </div>
      )}
      {error && <div style={{ ...text.hint, color: theme.color.dangerText, marginTop: 8 }}>{error}</div>}
      <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
        {onCancel && <button type="button" onClick={onCancel} disabled={busy} style={btnSecondary}>Cancel</button>}
        <button type="button" onClick={save} disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>
          {busy ? "Saving…" : "Record the review"}
        </button>
      </div>
    </div>
  );
}
